import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Brain,
  Wand2,
  Sparkles,
  FileText,
  Calendar,
  CheckCircle2,
  Loader,
  AlertCircle,
  Star,
  Zap,
  Trophy,
  Target,
  Plus,
  Upload,
  File,
  X,
  Share2
} from 'lucide-react';
import { Exam } from '../types/exam.types';
import { examApi } from '../services/examApi';
import { coursesApi } from '../services/api';
import { shareApi } from '../services/shareApi';
import { analytics } from '../services/analytics';
import { MixedText } from '../utils/rtl';
import { Card, CardHeader, CardTitle, CardContent, CardFooter, SpaceCard } from './ui/Card';
import { Button } from './ui/Button';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { DashboardContainer } from './layout/PageContainer';
import { CourseSelector } from './quick-actions/shared/CourseSelector';

const ExamAIGenerationView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const { courseId } = useParams<{ courseId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fromDashboard = searchParams.get('source') === 'dashboard';
  
  // Course handling
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [newCourseName, setNewCourseName] = useState('');
  const [isCreatingNewCourse, setIsCreatingNewCourse] = useState(false);
  const [courses, setCourses] = useState<any[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(true);

  // File upload state (for AI generation)
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Course materials state (for course library)
  const [courseMaterialFiles, setCourseMaterialFiles] = useState<File[]>([]);
  const [uploadingMaterials, setUploadingMaterials] = useState(false);
  const [materialUploadError, setMaterialUploadError] = useState<string | null>(null);
  const [isCourseMaterialsDragging, setIsCourseMaterialsDragging] = useState(false);
  const courseMaterialsInputRef = useRef<HTMLInputElement>(null);

  // State
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [examTitle, setExamTitle] = useState('');
  const [questionCount, setQuestionCount] = useState(3);
  const [difficultyLevel, setDifficultyLevel] = useState('medium');
  const [aiGuidance, setAiGuidance] = useState('');

  // Success state
  const [generatedExamId, setGeneratedExamId] = useState<number | null>(null);
  const [generatedExamCourseId, setGeneratedExamCourseId] = useState<number | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const buildShareUrl = useCallback((token?: string | null) => {
    if (!token) return null;
    return `${window.location.origin}/share/exam/${token}`;
  }, []);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  useEffect(() => {
    loadCourses();
    handleUrlParameters();
  }, []);

  const loadCourses = async () => {
    try {
      setLoadingCourses(true);
      const coursesData = await coursesApi.getAllCourses();
      setCourses(coursesData);
    } catch (err) {
      console.error('Error loading courses:', err);
      setError(t('common:errors.loading_failed'));
    } finally {
      setLoadingCourses(false);
    }
  };

  const handleUrlParameters = () => {
    // Check for course_id in URL parameters or route params
    const urlCourseId = searchParams.get('course_id') || courseId;
    if (urlCourseId) {
      const courseIdNum = parseInt(urlCourseId);
      if (!isNaN(courseIdNum)) {
        setSelectedCourseId(courseIdNum);
        setIsCreatingNewCourse(false);
      }
    }
  };

  const handleFileUpload = (files: FileList) => {
    const validFiles = Array.from(files).filter(file => {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();
      return fileType === 'application/pdf' || 
             fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
             fileName.endsWith('.pdf') || 
             fileName.endsWith('.docx');
    });

    if (validFiles.length !== files.length) {
      setError(t('common:errors.invalid_file_type'));
      return;
    }

    setUploadedFiles(prev => [...prev, ...validFiles]);
    setError(null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFileUpload(e.target.files);
    }
  };

  const handleDropzoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Course materials handlers
  const handleCourseMaterialsUpload = (files: FileList) => {
    const validFiles = Array.from(files).filter(file => {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();
      return fileType === 'application/pdf' ||
             fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
             fileName.endsWith('.pdf') ||
             fileName.endsWith('.docx');
    });

    if (validFiles.length !== files.length) {
      setMaterialUploadError(t('common:errors.invalid_file_type'));
      return;
    }

    setCourseMaterialFiles(prev => [...prev, ...validFiles]);
    setMaterialUploadError(null);
  };

  const handleCourseMaterialsInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleCourseMaterialsUpload(e.target.files);
    }
  };

  const handleCourseMaterialsDropzoneClick = () => {
    courseMaterialsInputRef.current?.click();
  };

  const handleCourseMaterialsDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsCourseMaterialsDragging(true);
  };

  const handleCourseMaterialsDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsCourseMaterialsDragging(false);
  };

  const handleCourseMaterialsDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsCourseMaterialsDragging(false);
    handleCourseMaterialsUpload(e.dataTransfer.files);
  };

  const removeCourseMaterialFile = (index: number) => {
    setCourseMaterialFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadCourseMaterials = async (courseId: number) => {
    if (courseMaterialFiles.length === 0) return;

    setUploadingMaterials(true);
    setMaterialUploadError(null);

    const uploadPromises = courseMaterialFiles.map(file =>
      examApi.uploadCourseMaterial(courseId, file, 'reference')
    );

    try {
      await Promise.all(uploadPromises);

      analytics.track('Course Materials Uploaded from AI Generation', {
        course_id: courseId,
        file_count: courseMaterialFiles.length,
        source: 'ai_exam_generation'
      });
    } catch (err) {
      console.error('Error uploading course materials:', err);
      setMaterialUploadError(t('exams:courseMaterials.uploadError'));
      analytics.trackError('Course Materials Upload Failed', {
        courseId,
        fileCount: courseMaterialFiles.length,
        source: 'ai_exam_generation'
      });
    } finally {
      setUploadingMaterials(false);
    }
  };

  const validateForm = () => {
    if (!selectedCourseId && (!isCreatingNewCourse || !newCourseName.trim())) {
      setError(t('common:errors.course_required'));
      return false;
    }
    if (!examTitle.trim()) {
      setError(t('exams:aiGeneration.errors.titleRequired'));
      return false;
    }
    if (uploadedFiles.length === 0) {
      setError(t('exams:aiGeneration.errors.noFilesUploaded'));
      return false;
    }
    if (questionCount < 1 || questionCount > 6) {
      setError(t('exams:aiGeneration.errors.invalidQuestionCount'));
      return false;
    }
    return true;
  };

  const handleGenerate = async () => {
    if (!validateForm()) return;

    let finalCourseId = selectedCourseId;

    setGenerating(true);
    setError(null);

    try {
      // Create course if needed
      if (isCreatingNewCourse && newCourseName.trim()) {
        const newCourse = await coursesApi.createCourse({ name: newCourseName.trim() });
        finalCourseId = newCourse.id;
        
        analytics.track('Course Created', {
          course_id: newCourse.id,
          course_name: newCourseName.trim(),
          created_from: 'ai_exam_generation'
        });
      }

      if (!finalCourseId) {
        throw new Error('No course selected or created');
      }

      // Generate AI exam with all uploaded files
      const response = await examApi.generateAIExamWithFiles(
        finalCourseId,
        uploadedFiles,
        {
          title: examTitle.trim(),
          questionCount,
          difficultyLevel,
          guidance: aiGuidance.trim() || undefined
        }
      );

      // Upload course materials if any
      if (courseMaterialFiles.length > 0) {
        await uploadCourseMaterials(finalCourseId);
      }

      setGeneratedExamId(response.id);
      setGeneratedExamCourseId(finalCourseId);
      setShowSuccess(true);

      analytics.track('AI Exam Generated', {
        course_id: finalCourseId,
        exam_id: response.id,
        uploaded_files: uploadedFiles.length,
        question_count: questionCount,
        difficulty_level: difficultyLevel,
        exam_title: examTitle,
        has_guidance: !!aiGuidance.trim(),
        is_new_course: isCreatingNewCourse
      });
    } catch (err: any) {
      console.error('Error generating exam:', err);
      
      // Skip handling if credit error was already handled by interceptor
      if (err?.name === 'CreditErrorHandled') {
        setGenerating(false); // Reset generating state for credit errors
        return;
      }
      
      setError(t('exams:aiGeneration.errors.generationFailed'));
      analytics.trackError('AI Exam Generation Failed', {
        course_id: finalCourseId,
        uploaded_files: uploadedFiles.length,
        question_count: questionCount,
        difficulty_level: difficultyLevel,
        error: err.message,
        is_new_course: isCreatingNewCourse
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleShareGeneratedExam = async () => {
    if (!generatedExamId) return;
    setShareError(null);
    setShareLoading(true);
    try {
      const response = await shareApi.enableExamShare(generatedExamId);
      const url = response.share_url || buildShareUrl(response.share_token);
      setShareUrl(url || null);
      if (url) {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 1500);
      }
    } catch (err) {
      console.error('Failed to share exam', err);
      setShareError(t('exams:share.error', 'Unable to create a share link right now.'));
    } finally {
      setShareLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const locale = isRTL ? 'he-IL' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <DashboardContainer>
        <div className="flex items-center justify-center h-64">
          <div className="w-16 h-16 bg-vibrant-orange/10 rounded-full flex items-center justify-center animate-pulse">
            <Brain className="w-8 h-8 text-vibrant-orange animate-spin" />
          </div>
        </div>
      </DashboardContainer>
    );
  }

  if (showSuccess && generatedExamId) {
    return (
      <DashboardContainer>
        <div className="space-y-8">
          {/* Success State */}
          <SpaceCard constellation="orion" className="text-center">
            <CardContent className="py-8 md:py-20">
              <div className="mb-4 md:mb-8 flex flex-col items-center">
                <div className="w-20 h-20 mb-4 md:mb-6 animate-bounce">
                  <img 
                    src="/astronaut_cool.png" 
                    alt="Success" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <CardTitle className="text-2xl md:text-3xl font-bold mb-4 font-hebrew text-center">
                  <MixedText>{t('exams:aiGeneration.success.title')}</MixedText>
                </CardTitle>
                <p className="text-base md:text-lg text-surface-secondary mb-2 font-hebrew text-center">
                  <MixedText>{t('exams:aiGeneration.success.description')}</MixedText>
                </p>
                <p className="text-surface-muted font-hebrew text-center">
                  <MixedText>{t('exams:aiGeneration.success.generatedQuestions', { count: questionCount })}</MixedText>
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate(`/course/${generatedExamCourseId}/exams/${generatedExamId}/practice`)}
                  variant="primary"
                  size="lg"
                  className="flex items-center justify-center"
                >
                  <MixedText>{t('exams:aiGeneration.success.startPracticing')}</MixedText>
                </Button>
                <Button
                  onClick={() => {
                    if (generatedExamCourseId) {
                      navigate(`/course/${generatedExamCourseId}/exams`);
                    } else {
                      navigate('/');
                    }
                  }}
                  variant="secondary"
                  size="lg"
                  className="flex items-center justify-center"
                >
                  <MixedText>{generatedExamCourseId ? t('exams:aiGeneration.success.backToExams') : t('common:buttons.back')}</MixedText>
                </Button>
                <Button
                  onClick={handleShareGeneratedExam}
                  variant="secondary"
                  size="lg"
                  icon={<Share2 className="w-4 h-4" />}
                  loading={shareLoading}
                  className="flex items-center justify-center"
                >
                  <MixedText>
                    {shareCopied
                      ? t('exams:share.copied', 'Link copied')
                      : t('exams:share.enable', 'Share read-only link')}
                  </MixedText>
                </Button>
              </div>
              {shareError && (
                <p className="text-sm text-red-500 mt-3">
                  <MixedText>{shareError}</MixedText>
                </p>
              )}
            </CardContent>
          </SpaceCard>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <div className="space-y-8">
        {/* Header */}
        <div className="space-y-6">
          {/* Breadcrumb Navigation */}
          <button
            onClick={() => {
              // If came from dashboard, always go back to dashboard
              if (fromDashboard) {
                navigate('/');
              } else {
                // Otherwise check if we have a course context
                const hasValidCourse = courseId || selectedCourseId;
                if (hasValidCourse) {
                  navigate(`/course/${courseId || selectedCourseId}/exams`);
                } else {
                  navigate('/');
                }
              }
            }}
            className={`flex items-center text-bg-secondary hover:text-vibrant-orange transition-colors group font-hebrew ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
          >
            {isRTL ? (
              <ArrowLeft className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
            ) : (
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
            )}
            <span className={`font-medium ${isRTL ? 'text-right' : ''}`}>
              🚀 {fromDashboard ? t('common:actions.back_to_dashboard') : (courseId ? t('common:actions.back_to_exams') : t('common:buttons.back'))}
            </span>
          </button>

          {/* Page Title */}
          <SpaceCard constellation="cassiopeia" className="group hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                <div className="w-16 h-16 bg-gradient-to-br from-vibrant-orange/20 to-muted-purple/20 rounded-2xl flex items-center justify-center relative">
                  <Brain className="w-8 h-8 text-vibrant-orange" />
                  <Zap className="w-4 h-4 text-muted-purple absolute -top-1 -right-1" />
                </div>
                <div className={`flex-1 ${isRTL ? 'text-right' : ''}`}>
                  <CardTitle className={`text-3xl font-bold mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{t('exams:aiGeneration.title')}</MixedText>
                  </CardTitle>
                  <p className={`text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{t('exams:aiGeneration.subtitle')}</MixedText>
                  </p>
                </div>
              </div>
            </CardHeader>
          </SpaceCard>
        </div>

        {/* Error Display */}
        {error && (
          <Card className="border-vibrant-orange/30 bg-vibrant-orange/10">
            <CardContent>
              <div className="flex items-center">
                <AlertCircle className={`w-5 h-5 text-vibrant-orange ${isRTL ? 'ml-3' : 'mr-3'}`} />
                <p className={`text-vibrant-orange font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  ⚠️ <MixedText>{error}</MixedText>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Generating State */}
        {generating && (
          <SpaceCard constellation="orion" className="group hover:shadow-xl transition-all duration-300">
            <CardContent className="text-center space-y-4 py-4 md:py-8">
              <div className="w-16 h-16 mx-auto">
                <img 
                  src="/astronaut_cool.png" 
                  alt="Generating..." 
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <CardTitle className="text-xl font-bold mb-2 font-hebrew text-center">
                  <MixedText>{t('exams:aiGeneration.generating.title')}</MixedText>
                </CardTitle>
                <CardContent className="text-surface-secondary font-hebrew mb-4 text-center">
                  <MixedText>{t('exams:aiGeneration.generating.description')}</MixedText>
                </CardContent>
                <p className="text-surface-muted font-hebrew text-sm text-center">
                  <MixedText>{t('exams:aiGeneration.generating.pleaseWait')}</MixedText>
                </p>
              </div>
              <div className="flex justify-center pt-2 md:pt-4">
                <div className="flex items-center justify-center px-6 py-3 bg-muted-purple/10 border border-muted-purple/30 rounded-lg">
                  <LoadingSpinner 
                    variant="satellite" 
                    size="sm" 
                    color="purple" 
                    className="mr-3" 
                  />
                  <span className="text-muted-purple font-hebrew">
                    <MixedText>{t('exams:aiGeneration.generating.title')}</MixedText>
                  </span>
                </div>
              </div>
            </CardContent>
          </SpaceCard>
        )}

        {/* Check if no exams available */}
        {/* Main Form - Only show when not generating */}
        {!generating && (
          <SpaceCard constellation="orion">
            <CardContent>
              <div className="max-w-3xl mx-auto space-y-4 md:space-y-8">
                {/* Course Selection */}
                <CourseSelector
                  selectedCourseId={selectedCourseId}
                  newCourseName={newCourseName}
                  onCourseSelect={setSelectedCourseId}
                  onNewCourseNameChange={setNewCourseName}
                  isCreatingNew={isCreatingNewCourse}
                  onToggleCreateNew={() => setIsCreatingNewCourse(!isCreatingNewCourse)}
                  existingCourses={courses}
                  isRTL={isRTL}
                  loading={loadingCourses}
                />

                {/* Exam Title */}
                <div>
                  <label className={`block text-lg font-semibold text-surface-primary mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    📝 <MixedText>{t('exams:aiGeneration.form.examTitle')}</MixedText> *
                  </label>
                  <input
                    type="text"
                    value={examTitle}
                    onChange={(e) => setExamTitle(e.target.value)}
                    placeholder={t('exams:aiGeneration.form.examTitlePlaceholder')}
                    className={`w-full px-4 py-3 text-lg border-2 border-surface-muted/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-vibrant-orange/50 focus:border-vibrant-orange transition-colors bg-white/50 backdrop-blur-sm ${isRTL ? 'text-right' : ''}`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>

                {/* File Upload Area */}
                <div>
                  <label className={`block text-lg font-semibold text-surface-primary mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    📁 <MixedText>{t('exams:aiGeneration.form.uploadDocuments')}</MixedText> *
                  </label>
                  <div
                    className={`border-2 border-dashed border-surface-muted/30 rounded-xl p-4 md:p-8 text-center cursor-pointer transition-colors duration-200 ${
                      isDragging
                        ? 'border-vibrant-orange/50 bg-vibrant-orange/5'
                        : 'hover:border-vibrant-orange/50 hover:bg-vibrant-orange/5'
                    }`}
                    onClick={handleDropzoneClick}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.docx"
                    onChange={handleFileInputChange}
                    ref={fileInputRef}
                    className="hidden"
                  />
                  <div className="space-y-4">
                    <Upload className="w-10 h-10 text-surface-muted mx-auto" />
                    <div>
                      <p className="text-sm text-surface-secondary font-hebrew">
                        <MixedText>{t('exams:aiGeneration.form.dragAndDropFiles')}</MixedText>
                      </p>
                      <p className="text-xs text-surface-muted font-hebrew mt-1">
                        <MixedText>{t('exams:aiGeneration.form.supportedFormats')}</MixedText>
                      </p>
                    </div>
                  </div>
                  </div>
                </div>

                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <div className="space-y-3">
                    <h4 className={`text-lg font-semibold text-surface-primary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      <MixedText>{t('exams:aiGeneration.form.uploadedFiles')}</MixedText>
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {uploadedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center bg-surface-muted/10 text-surface-secondary text-sm px-3 py-1.5 rounded-full"
                        >
                          <File className="w-4 h-4 mr-1" />
                          {file.name}
                          <button
                            onClick={() => removeFile(index)}
                            className="ml-2 text-surface-muted hover:text-vibrant-orange"
                            title={t('exams:aiGeneration.form.removeFile')}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Course Materials Upload (Optional) */}
                <div>
                  <label className={`block text-lg font-semibold text-surface-primary mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    📚 <MixedText>{t('exams:courseMaterials.upload.title', 'Upload Course Materials')}</MixedText> ({t('common:labels.optional', 'Optional')})
                  </label>
                  <p className={`text-sm text-surface-secondary mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{t('exams:courseMaterials.upload.description', 'Upload additional reference materials that can be used for future exams in this course.')}</MixedText>
                  </p>
                  <div
                    className={`border-2 border-dashed border-surface-muted/30 rounded-xl p-4 md:p-8 text-center cursor-pointer transition-colors duration-200 ${
                      isCourseMaterialsDragging
                        ? 'border-muted-purple/50 bg-muted-purple/5'
                        : 'hover:border-muted-purple/50 hover:bg-muted-purple/5'
                    }`}
                    onClick={handleCourseMaterialsDropzoneClick}
                    onDragOver={handleCourseMaterialsDragOver}
                    onDragLeave={handleCourseMaterialsDragLeave}
                    onDrop={handleCourseMaterialsDrop}
                  >
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.docx"
                      onChange={handleCourseMaterialsInputChange}
                      ref={courseMaterialsInputRef}
                      className="hidden"
                    />
                    <div className="space-y-4">
                      {uploadingMaterials ? (
                        <>
                          <Loader className="w-10 h-10 text-muted-purple mx-auto animate-spin" />
                          <p className="text-sm text-muted-purple font-hebrew">
                            <MixedText>{t('common:messages.uploading')}</MixedText>
                          </p>
                        </>
                      ) : (
                        <>
                          <Upload className="w-10 h-10 text-surface-muted mx-auto" />
                          <div>
                            <p className="text-sm text-surface-secondary font-hebrew">
                              <MixedText>{t('exams:courseMaterials.dragDrop', 'Drag & drop materials here or click to browse')}</MixedText>
                            </p>
                            <p className="text-xs text-surface-muted font-hebrew mt-1">
                              <MixedText>{t('common:upload.supportedFormats', 'PDF, DOCX')}</MixedText>
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Uploaded Course Materials List */}
                  {courseMaterialFiles.length > 0 && (
                    <div className="mt-4 space-y-3">
                      <h4 className={`text-lg font-semibold text-surface-primary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                        📎 <MixedText>{t('exams:courseMaterials.uploadedFiles', 'Uploaded Materials')}</MixedText>
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {courseMaterialFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center bg-muted-purple/10 text-surface-secondary text-sm px-3 py-1.5 rounded-full"
                          >
                            <File className="w-4 h-4 mr-1" />
                            {file.name}
                            <button
                              onClick={() => removeCourseMaterialFile(index)}
                              className="ml-2 text-surface-muted hover:text-vibrant-orange"
                              title={t('exams:aiGeneration.form.removeFile')}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {materialUploadError && (
                    <div className="mt-4 p-3 bg-vibrant-orange/10 border border-vibrant-orange/30 rounded-lg">
                      <p className={`text-vibrant-orange text-sm ${isRTL ? 'text-right' : ''}`}>
                        <MixedText>{materialUploadError}</MixedText>
                      </p>
                    </div>
                  )}
                </div>

                {/* Question Count */}
                <div>
                  <label className={`block text-lg font-semibold text-surface-primary mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    🎯 <MixedText>{t('exams:aiGeneration.form.questionCount')}</MixedText>
                  </label>
                  <p className={`text-sm text-surface-secondary mb-2 md:mb-4 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{t('exams:aiGeneration.form.questionCountDescription')}</MixedText>
                  </p>
                  
                  <div className={`flex gap-2 md:gap-3 ${isRTL ? 'justify-end' : ''}`}>
                    {[1, 2, 3, 4, 5, 6].map((count) => (
                      <button
                        key={count}
                        onClick={() => setQuestionCount(count)}
                        className={`w-10 h-10 md:w-12 md:h-12 rounded-xl border-2 font-semibold transition-all duration-200 ${
                          questionCount === count
                            ? 'border-vibrant-orange/50 bg-vibrant-orange/10 text-vibrant-orange'
                            : 'border-surface-muted/30 bg-white/50 text-surface-secondary hover:border-vibrant-orange/50'
                        }`}
                      >
                        {count}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Difficulty Level */}
                <div>
                  <label className={`block text-lg font-semibold text-surface-primary mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    ⭐ <MixedText>{t('exams:aiGeneration.form.difficultyLevel')}</MixedText>
                  </label>
                  <p className={`text-sm text-surface-secondary mb-2 md:mb-4 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{t('exams:aiGeneration.form.difficultyDescription')}</MixedText>
                  </p>
                  
                  <div className={`grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 ${isRTL ? 'space-x-reverse' : ''}`}>
                    {['easy', 'medium', 'hard'].map((level) => (
                      <Card
                        key={level}
                        onClick={() => setDifficultyLevel(level)}
                        className={`cursor-pointer border-2 transition-all duration-200 hover:shadow-lg ${
                          difficultyLevel === level
                            ? 'border-vibrant-orange/50 bg-vibrant-orange/5 shadow-lg shadow-vibrant-orange/20'
                            : 'border-surface-muted/30 hover:border-vibrant-orange/50'
                        }`}
                      >
                        <CardContent className="p-4 text-center">
                          <div className={`w-8 h-8 mx-auto mb-2 rounded-lg flex items-center justify-center ${
                            difficultyLevel === level 
                              ? 'bg-vibrant-orange/20' 
                              : 'bg-surface-muted/10'
                          }`}>
                            <Star className={`w-4 h-4 ${
                              difficultyLevel === level 
                                ? 'text-vibrant-orange' 
                                : 'text-surface-muted'
                            }`} />
                          </div>
                          <span className={`font-semibold text-sm font-hebrew ${
                            difficultyLevel === level 
                              ? 'text-vibrant-orange' 
                              : 'text-surface-primary'
                          }`}>
                            <MixedText>{t(`exams:difficulty.${level}`)}</MixedText>
                          </span>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* AI Guidance Section */}
                <Card className="border-muted-purple/30 bg-muted-purple/5">
                  <CardContent className="p-3 md:p-4">
                    <label className={`block text-lg font-semibold text-surface-primary mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      🤖 <MixedText>{t('exams:aiGeneration.form.aiGuidance')}</MixedText>
                    </label>
                    <p className={`text-xs text-muted-purple mb-2 md:mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      <MixedText>{t('exams:aiGeneration.form.aiGuidanceDescription')}</MixedText>
                    </p>
                    <textarea
                      value={aiGuidance}
                      onChange={(e) => setAiGuidance(e.target.value)}
                      placeholder={t('exams:aiGeneration.form.aiGuidancePlaceholder')}
                      className={`w-full h-20 md:h-24 px-3 py-2 text-sm border-2 border-muted-purple/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-muted-purple/50 focus:border-muted-purple transition-colors resize-none bg-white/50 backdrop-blur-sm ${isRTL ? 'text-right' : ''}`}
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </CardContent>
                </Card>

                {/* Actions */}
                <CardFooter className="pt-4 md:pt-6 border-t border-surface-muted/20">
                  <div className="flex flex-col space-y-3 w-full">
                    {/* Availability Notice */}
                    <div className={`text-center text-sm text-surface-muted font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      <span className="inline-flex items-center">
                        💡 <MixedText>{t('exams:aiGeneration.form.availability_notice')}</MixedText>
                      </span>
                    </div>

                    <Button
                      onClick={handleGenerate}
                      disabled={generating || uploadedFiles.length === 0 || !examTitle.trim() || (!selectedCourseId && (!isCreatingNewCourse || !newCourseName.trim()))}
                      variant="primary"
                      size="lg"
                      className="w-full py-3 md:py-4 text-base md:text-lg flex items-center justify-center"
                    >
                      {generating ? (
                        <>
                          <LoadingSpinner 
                            variant="satellite" 
                            size="sm" 
                            color="primary" 
                            className="mr-2" 
                          />
                          <span>
                            <MixedText>{t('exams:aiGeneration.generating.title')}</MixedText>
                          </span>
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-5 h-5 mr-2" />
                          <MixedText>{t('exams:aiGeneration.form.generateExam')}</MixedText>
                        </>
                      )}
                    </Button>

                    <Button
                      onClick={() => {
                        // If came from dashboard, always go back to dashboard
                        if (fromDashboard) {
                          navigate('/');
                        } else {
                          // Otherwise check if we have a course context
                          const hasValidCourse = courseId || selectedCourseId;
                          if (hasValidCourse) {
                            navigate(`/course/${courseId || selectedCourseId}/exams`);
                          } else {
                            navigate('/');
                          }
                        }
                      }}
                      variant="secondary"
                      size="lg"
                      disabled={generating}
                      className="w-full"
                    >
                      <MixedText>{t('exams:aiGeneration.form.cancel')}</MixedText>
                    </Button>
                  </div>
                </CardFooter>
              </div>
            </CardContent>
          </SpaceCard>
        )}
      </div>
    </DashboardContainer>
  );
};

export default ExamAIGenerationView; 
